import { ShieldCheck, AlertTriangle, AlertCircle, MessageCircle } from 'lucide-react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const WHATSAPP_NUMBER = '5575981872440';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Olá, vim do site de nódulo e gostaria de agendar uma consulta com dr. Mateus')}`;

const noduloTypes = [
  {
    key: 'benigno',
    icon: ShieldCheck,
    title: 'Nódulos Benignos',
    subtitle: 'Os Que Não Oferecem Risco',
    badge: 'Baixo Risco',
    badgeColor: '#2E7D32',
    bgColor: '#2E7D32',
    borderColor: '#4CAF50',
    desc: 'Existem características que indicam que o nódulo é benigno e não oferece risco imediato. Se seu nódulo se encaixa nessas características, a tranquilidade é justificada. Mas mesmo assim, acompanhamento regular é importante.',
    characteristics: [
      'Tamanho menor que 8mm',
      'Bordas lisas e bem definidas',
      'Forma redonda ou oval',
      'Densidade homogênea',
      'Sem crescimento em tomografias seriadas',
      'Presença de calcificação central',
    ],
    nextStep: 'Acompanhamento radiológico periódico conforme orientação do especialista',
    examples: 'Cicatrizes de infecções antigas, inflamações crônicas, hamartomas',
  },
  {
    key: 'indeterminado',
    icon: AlertTriangle,
    title: 'Nódulos Indeterminados',
    subtitle: 'Quando Precisa de Seguimento',
    badge: 'Vigilância Necessária',
    badgeColor: '#E65100',
    bgColor: '#F59E0B',
    borderColor: '#F59E0B',
    desc: 'Indeterminado significa: não temos características definitivas de benignidade, mas também não temos dados alarmantes que sugiram malignidade. A maioria dos nódulos indeterminados NÃO evolui para câncer. Mas precisam de vigilância profissional.',
    characteristics: [
      'Tamanho entre 8–20mm',
      'Bordas levemente irregulares',
      'Densidade heterogênea',
      'Sem calcificação',
      'Sem história clara de infecção',
    ],
    nextStep: 'Protocolo de acompanhamento: tomografias seriadas em intervalos definidos (3, 6 ou 12 meses)',
    examples: 'Protocolo baseado nas diretrizes da Fleischner Society',
  },
  {
    key: 'suspeito',
    icon: AlertCircle,
    title: 'Nódulos Suspeitos',
    subtitle: 'Quando Biópsia É Indicada',
    badge: 'Avaliação Prioritária',
    badgeColor: '#C62828',
    bgColor: '#D32F2F',
    borderColor: '#EF5350',
    desc: 'Existem características que elevam a suspeita de malignidade. Nesses casos, o objetivo é fechar diagnóstico rapidamente para iniciar o tratamento. E aqui é onde o cirurgião torácico entra — com expertise em técnicas de biópsia segura e minimamente invasiva.',
    characteristics: [
      'Tamanho maior que 20mm',
      'Bordas irregulares ou espiculadas',
      'Densidade heterogênea com “vidro fosco”',
      'Crescimento constante em tomografias seriadas',
      'Linfonodos regionais aumentados',
      'Histórico de câncer prévio ou tabagismo intenso',
    ],
    nextStep: 'Biópsia pulmonar para confirmar ou descartar malignidade — com orientação completa do cirurgião',
    examples: 'Diagnóstico precoce = maiores chances de cura',
  },
];

export default function NoduloInfoSection() {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section
      id="nodulo"
      ref={ref}
      className={`py-24 md:py-32 bg-[#EEF4FF] transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">

        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center bg-[#0D47A1]/10 text-[#0D47A1] rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            Entenda a Condição
          </div>
          <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-[#212121] mb-6 leading-tight">
            O Que É Um Nódulo Pulmonar?<br />Entenda Sem Pânico
          </h2>
          <p className="text-base md:text-lg text-[#212121]/70 leading-relaxed">
            Nódulo pulmonar é um achado comum em exames de imagem (tomografia, radiografia).
            Mas “comum” não significa “grave”. A maioria dos nódulos descobertos são benignos.
            Entenda o que você tem e por que precisa de um especialista.
          </p>
        </div>

        {/* Definition callout + lung illustration side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 items-stretch">

          {/* Lung illustration — same height as blue card, caption below */}
          <div className="flex flex-col justify-center">
            <div className="relative w-full rounded-3xl overflow-hidden shadow-xl h-full">
              <img
                src="/images/nodulo_pulmonar_ilustracao.png"
                alt="Ilustração de nódulo pulmonar nos pulmões — diagnóstico de nódulo pulmonar"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <p className="text-[#212121]/50 text-xs text-center mt-3 italic">
              Nódulo pulmonar visualizado em imagem 3D — pequena lesão no tecido pulmonar
            </p>
          </div>

          {/* Definition callout */}
          <div className="bg-[#0D47A1] rounded-2xl p-8 text-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                <ShieldCheck size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-poppins font-bold text-xl mb-3">O Que É Um Nódulo Pulmonar?</h3>
                <p className="text-white/85 text-base leading-relaxed mb-3">
                  Um nódulo pulmonar é uma pequena lesão no tecido do pulmão — medindo até 3cm de diâmetro
                  (a partir deste tamanho é chamado de Massa Pulmonar). Na maioria das vezes não causa sintoma
                  e é detectado acidentalmente em exames de imagem feitos por outras razões.
                </p>
                <div className="flex flex-col gap-3 mt-4">
                  <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-3">
                    <ShieldCheck size={18} className="text-green-300 flex-shrink-0" />
                    <span className="text-sm text-white/90">Nódulos pequenos com bordas bem definidas são frequentemente benignos</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-3">
                    <AlertCircle size={18} className="text-yellow-300 flex-shrink-0" />
                    <span className="text-sm text-white/90">Alguns nódulos precisam de avaliação mais aprofundada para descartar malignidade</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* 3 type cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {noduloTypes.map((type) => {
            const Icon = type.icon;
            return (
              <div
                key={type.key}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                style={{ borderTop: `4px solid ${type.borderColor}` }}
              >
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${type.bgColor}15` }}
                    >
                      <Icon size={22} style={{ color: type.bgColor }} />
                    </div>
                    <div>
                      <h3 className="font-poppins font-bold text-lg text-[#212121] leading-tight">{type.title}</h3>
                      <span
                        className="text-xs font-semibold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${type.bgColor}15`, color: type.badgeColor }}
                      >
                        {type.badge}
                      </span>
                    </div>
                  </div>

                  <p className="text-[#212121]/65 text-sm leading-relaxed mb-5">{type.desc}</p>

                  <div className="mb-5">
                    <p className="text-xs font-semibold text-[#212121]/50 uppercase tracking-wide mb-2">Características</p>
                    <ul className="space-y-1.5">
                      {type.characteristics.map((char, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span
                            className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                            style={{ backgroundColor: type.bgColor }}
                          />
                          <span className="text-[#212121]/70 text-sm">{char}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div
                    className="rounded-xl p-3 text-sm"
                    style={{ backgroundColor: `${type.bgColor}10`, borderLeft: `3px solid ${type.borderColor}` }}
                  >
                    <p className="text-xs font-semibold mb-1" style={{ color: type.badgeColor }}>PRÓXIMO PASSO</p>
                    <p className="text-[#212121]/75 text-sm">{type.nextStep}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#0D47A1] text-white font-poppins font-bold px-10 py-4 rounded-full hover:bg-[#00897B] transition-colors duration-200 shadow-lg hover:-translate-y-0.5"
          >
            <MessageCircle size={18} />
            AGENDAR AVALIAÇÃO COM O ESPECIALISTA
          </a>
        </div>

      </div>
    </section>
  );
}
